<template>
	<fieldset class="m-radioSegment">
		<legend class="m-radioSegment__legend">{{ label }}</legend>

		<p v-if="description" :id="descId" class="m-radioSegment__description">
			{{ description }}
		</p>

		<div class="m-radioSegment__group" :aria-describedby="description ? descId : null">
			<div
				v-for="(opt, index) in normalizedOptions"
				:key="`${idBase}-${index}`"
				class="m-radioSegment__item"
			>
				<input
					class="m-radioSegment__input"
					:type="'radio'"
					:id="`${idBase}-${index}`"
					:name="name"
					v-model="selected"
					:value="opt.value"
				/>
				<label class="m-radioSegment__label" :for="`${idBase}-${index}`">
					{{ opt.label }}
				</label>
			</div>
		</div>
	</fieldset>
</template>

<script setup>
import { computed, watch, nextTick } from 'vue';

const props = defineProps( {
	label: {
		type: String,
		required: true,
	},
	name: {
		type: String,
		required: true,
	},
	description: {
		type: String,
		default: '',
	},
	options: {
		type: Array,
		required: true,
		// Accept strings or objects { label, value }
		validator: ( value ) => Array.isArray( value ) && value.length > 0,
	},
	modelValue: {
		type: [ String, Number, null ],
		default: null,
	},
} );

const emit = defineEmits( [ 'update:modelValue' ] );

const normalizedOptions = computed( () => {
	return props.options.map( ( option ) => {
		if ( typeof option === 'object' ) {
			return { label: option.label, value: option.value };
		}
		return { label: String( option ), value: option };
	} );
} );

const idBase = `radio-segment-${Math.random().toString( 36 ).slice( 2 )}`;
const descId = `${idBase}-desc`;

const selected = computed( {
	get() {
		return props.modelValue;
	},
	set( value ) {
		// Coerce numeric strings back to numbers when original option was a number
		const match = normalizedOptions.value.find( ( o ) => String( o.value ) === String( value ) );
		const coerced = match && typeof match.value === 'number' ? Number( value ) : value;
		emit( 'update:modelValue', coerced );
	},
} );

// Watch for changes in options array and ensure modelValue is preserved if still valid
watch( normalizedOptions, ( newOptions ) => {
	const currentValue = props.modelValue;
	
	if ( currentValue === null || currentValue === undefined ) {
		return;
	}
	
	// Check if current value is still valid in the new options
	const isValid = newOptions.some( ( opt ) => {
		return String( opt.value ) === String( currentValue );
	} );
	
	if ( isValid ) {
		// Value is still valid, explicitly emit it to ensure Vue's v-model stays in sync
		// Force a re-emit to ensure Vue's v-model stays in sync
		nextTick( () => {
			const match = newOptions.find( ( o ) => String( o.value ) === String( currentValue ) );
			if ( match ) {
				const coerced = typeof match.value === 'number' ? Number( currentValue ) : currentValue;
				emit( 'update:modelValue', coerced );
			}
		} );
	}
	// If value is not valid, don't change it - let the parent component handle it
}, { immediate: false } );
</script>


